import _ from 'lodash';
import moment from 'moment-timezone';

import { enums } from 'console-kit';

import RefData from 'ref-data';
import CapabilityGate, { capabilities } from './Services/capability-gate';

import {
  MultipleChoiceData,
  MultipleChoiceScoreQuizData,
} from '../ModuleEditor/components/common/formFields/models/MultipleChoiceData';

import { QuizCategoryResultData } from '../ModuleEditor/components/common/formFields/models/QuizCategoryResultData';

import { locale } from '../ModuleEditor/util/locale-wrapper';

import {
  designerTypes,
  layoutPlacements,
  quizTypes,
  spinSpeed,
  sharingNetworks,
  sharingFields,
  sharingUrlValues,
  ratingIcons,
} from './enums';

import FIELD_TYPES from './formFields/fieldTypes';

import { ADDRESS_FORMAT } from './formFields/addressFormats';
import { VALIDATION_TYPE } from './formFields/addressValidationTypes';
import { AGE_COMPARISON } from './formFields/ageComparison';
import AUTO_ADVANCE from './formFields/autoAdvance.js';
import { BREAK_TYPE } from './formFields/breakType';
import { COUNTRY } from './formFields/countries';
import { DATE_FORMATS } from './formFields/dateFormats';
import { DAYS_ALL_STR } from './formFields/days';
import { EMAIL_VALIDATION_TYPES } from './formFields/emailValidationTypes';
import { FIELD_PROFILES } from './formFields/fieldProfiles.js';
import { FIELD_SIZINGS } from './formFields/fieldSizing';
import FIELD_TEMPLATES from './formFields/fieldTemplates';
import { FILTER_SCOPE } from './formFields/filterScopes';
import IMAGE_FITS from './formFields/imageFits';
import { MAX_LENGTH_UNITS } from './formFields/maxLengthUnits';
import MULTIPLE_RESULTS_LAYOUT_TYPE from './formFields/multipleResultsLayoutTypes.js';
import { MX_POLL_DISPLAY } from './formFields/mxPollDisplay.js';
import { NAME_FORMATS } from './formFields/nameFormats';
import { OPT_IN_CONSENT } from './formFields/optinConsents';
import { OPT_IN_PRESENTATION } from './formFields/optinPresentation';
import { MULTI_OPT_IN_SCOPE, OPT_IN_SCOPE } from './formFields/optinScopes';
import { PHONE_FORMAT } from './formFields/phoneFormats';
import { PROGRESSIVE_FILTER_SCOPE } from './formFields/progressiveFilterScopes';
import { RESPONSIVE_SIZES } from './formFields/responsiveSizes';
import { SHOW_FIELDS_TO } from './formFields/showFieldTo';
import { TERM_DISPLAY } from './formFields/termDisplay';
import TILE_STYLES from './formFields/tileStyles';
import { UPLOAD_FIELD_TYPE } from './formFields/uploadFieldTypes';
import VALIDATION_MODE from './formFields/validationModes';

const defaultCountdownDate = moment().add(24, 'hours').tz('GMT');

const MODULE_TYPES = {
  // GENERAL
  HEADLINE:      'headline',
  RICH_HEADLINE: 'richHeadline',
  CALLOUT:       'callout',
  IMAGE:         'image',
  VIDEO:         'video',
  IMAGE_GALLERY: 'gallery', // was GALLERY in ME
  VIDEO_GALLERY: 'videoGallery',
  SHARING:       'sharing', // not footer
  MX_SHARING:    'mxSharing',
  LINK:          'link',
  HTML:          'html',
  PROMOTION:     'promotion',
  COUNTDOWN:     'countdown',
  MX_COUNTDOWN:  'mxCountdown',

  // SOCIAL
  COLLAGE:                 'collage',
  SOCIAL_CONTENT_WALL:     'socialContentWall',
  SOCIAL_CONTENT_CAROUSEL: 'socialContentCarousel',
  AVATAR_WALL:             'socialContentMosaic', // was SOCIAL_CONTENT_MOSAIC in PD
  SOCIAL_PLUGIN:           'socialPlugin', //Deprecated
  TILT:                    'tilt', //Deprecated
  MOMENTS:                 'moments', //Deprecated

  // PARTICIPATION
  FORM:             'form',
  ENTRIES:          'entries', // was UGC_ENTRIES in PD
  VOTING:           'voting',
  GOAL:             'goal',
  SCORE_RESULT:     'scoreResult',
  MX_RESULT:        'mxScoreResult', //[WYN-6898] new MX specific module for the new results requirements
  LEADERBOARD:      'leaderboardQuiz', // was LEADERBOARD_QUIZ //Deprecated
  POLL_RESULTS:     'metric', // was METRIC in PD
  MX_POLL_RESULTS:  'mxPollResults', // nextgen poll results
  MY_REWARDS:       'myRewards', //legacy reward module, currently used in macro experiences
  MX_MY_REWARDS:    'mxMyRewards',
  MX_IMAGE_GALLERY: 'mxImageGallery',

  // NOT IN PAGE DESIGNER MODULE LIST
  CAMPAIGN_HEADER: 'campaignHeader', // a valid module type, but added dynamically, not in PD mod list

  // Deprecated, but still need to support existing modules
  PARTICIPANT_WALL: 'avatarWall', // aka Participants // deprecated
};

MODULE_TYPES.ALL = _.values(MODULE_TYPES);

MODULE_TYPES.DEPRECATED_LIST = [
  MODULE_TYPES.LEADERBOARD,
  MODULE_TYPES.PARTICIPANT_WALL,
  MODULE_TYPES.SOCIAL_PLUGIN,
  MODULE_TYPES.MOMENTS,
  MODULE_TYPES.TILT,
];

/**
 * Defining a list of module groups, where we can enforce more restrictions.
 */
const MODULE_CATEGORIES = {
  QUIZ:          'Quiz',
  GAME:          'Game',
  STANDARD:      'Standard',
  GENERAL:       'General',
  SOCIAL:        'Social',
  PARTICIPATION: 'Participation',
  CONTENT:       'Content',
  USER:          'User',
};

/**
 * In a specific group, we will list multiple module types, and current restriction is to only allow one of those modules to be used on the form. The intention is to support one game on the form only.
 */
const MODULE_GROUP_RESTRICTION = {
  [MODULE_CATEGORIES.GAME]: {
    maxModuleCount: 1,
    packageModules: [FIELD_TYPES.SPIN_THE_WHEEL_GAME],
    maxMessage:     'You can only use one Game module on this page.',
  },
};

const defaultSharingConfig = {
  default: {
    action:    'share',
    byline:    'Configure the default sharing options fans will see when they share content from your experience',
    context:   'campaign',
    data:      [{}],
    enabled:   'false',
    isDefault: true,
    network:   'default',
    title:     'Default share prompt',
  },
  facebookConfirmation: {
    action:  'share',
    byline:  'Configure the sharing dialog fans will see when they arrive on the confirmation page of your experience',
    context: 'confirmation',
    data:    [{}],
    enabled: 'false',
    network: 'facebook',
    title:   'Facebook confirmation share prompt',
  },
  facebookShare: {
    action:  'share',
    byline:  'Configure the sharing dialog fans will see when they click a Facebook share button',
    context: 'campaign',
    data:    [{}],
    enabled: 'false',
    network: 'facebook',
    title:   'Facebook share prompt',
  },
  pinterestShare: {
    action:  'share',
    byline:  'Configure the Pinterest message fans will see when they click a Pin It button',
    context: 'campaign',
    data:    [{}],
    enabled: 'false',
    network: 'pinterest',
    title:   'Pinterest share prompt',
  },
  twitterShare: {
    action: 'share',
    byline:
      'Configure the message users will see when they click a X share button. A link to your experience or custom link will be added to the end of your post.',
    context: 'campaign',
    data:    [{}],
    enabled: 'false',
    network: 'twitter',
    title:   'X share prompt',
  },
};

// TODO: remove legacyIcon and filtering in onEditModule in DragSourceConnector once
// the new module editors roll out.  This is needed because legacy module editors are
// expecting an icon character vs. an icon classname.
const moduleData = {
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.CALLOUT]: {
    icon:            enums.icons.CTA,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:             'Callout',
      tooltip:                 'Call-to-action with image and copy',
      moduleDescription:       'Add copy, images and call to action',
      shareStyle:              'icon',
      target:                  '_blank',
      template:                'template:common:callout:item.postcard.bottom',
      useShare:                'nosharing',
      sharingConfig:           defaultSharingConfig,
      style:                   'std',
      copyTextAlignment:       'middle center',
      backgroundImageAssetRef: '',
      inverted:                false,
      displayConditions:       {},
    },
    sampleData: {
      featureImageAssetRef: 'asset:image:256x256:/images/9010/07186f42-1897-4e7f-9ee5-aabd48d27f86/cat_purr.png',
      heading:              'Callout',
      link:                 'route:entry',
      linkText:             'CTA',
      moduleDescription:    'Add copy, images and call to action',
      moduleTitle:          'Callout',
      byline:               'Lorem ipsum dolor',
      description:          'Mauris imperdiet risus in nulla posuere, sed porta orci dictum.',
      shareStyle:           'icon',
      target:               '_blank',
      template:             'template:common:callout:item.postcard.left',
      tooltip:              'Call-to-action with image and copy',
      useShare:             'nosharing',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.COLLAGE]: {
    icon:            enums.icons.GRID_LARGE,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:          'Collage',
      tooltip:              'Customize your own layout to display social content',
      moduleDescription:    'Add a social collage',
      template:             'template:common:collage:custom.medialayer', // no variant, add based on tile size dynamically
      ctaDisplayItemRender: 'overlay',
      detailTemplate:       'media',
      maxItems:             300,
      layouts:              {
        lg: [
          {
            x: 0,
            y: 0,
            w: 7,
            h: 7,
            i: '0',
          },
          {
            x: 0,
            y: 7,
            w: 7,
            h: 7,
            i: '1',
          },
          {
            x: 7,
            y: 0,
            w: 14,
            h: 14,
            i: '2',
          },
          {
            x: 21,
            y: 0,
            w: 7,
            h: 7,
            i: '3',
          },
          {
            x: 21,
            y: 7,
            w: 7,
            h: 7,
            i: '4',
          },
        ],
      },
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.POLL_RESULTS]: {
    icon:            enums.icons.RESULTS,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Classic Poll Results',
      tooltip:           'Display the results of a Classic Poll',
      moduleDescription: 'Display the results of a Classic Poll',
      target:            '_blank',
    },
  },

  [MODULE_TYPES.MX_POLL_RESULTS]: {
    icon:            enums.icons.RESULTS,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Poll Results',
      tooltip:           'Display the results of the Polls',
      moduleDescription: 'Display the results of the Polls',
      target:            '_blank',
      style:             'std',
      displayType:       MX_POLL_DISPLAY.PERCENTAGE,
      copyTextAlignment: `${enums.positions.verticalAlign.MIDDLE} ${enums.textAlignments.LEFT}`,
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.HEADLINE]: {
    icon:            enums.icons.TEXT,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Headline',
      tooltip:           'Major heading',
      moduleDescription: 'Set headline, byline and image',
      template:          'template:common:headline:item.postcard.bottom',
      inverted:          false,
      displayConditions: {},
    },
    sampleData: {
      byline:            'Your byline here',
      heading:           'Your heading here',
      moduleTitle:       'Text',
      tooltip:           'Add text',
      moduleDescription: 'Add a heading and body copy',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.RICH_HEADLINE]: {
    icon:            enums.icons.TEXT,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Headline',
      tooltip:           'Major heading',
      moduleDescription: 'Set headline, byline and image',
      template:          'template:common:headline:item.postcard.bottom',
      displayConditions: {},
    },
    sampleData: {
      byline:            'Your byline here',
      heading:           'Your heading here',
      moduleTitle:       'Headline',
      tooltip:           'Major heading',
      moduleDescription: 'Set headline, byline and image',
      template:          'template:common:headline:item.postcard.bottom',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.IMAGE]: {
    icon:            enums.icons.IMAGE,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:             'Image',
      tooltip:                 'Single image',
      moduleDescription:       'Feature an image and caption',
      images:                  [{}],
      backgroundImageAssetRef: '',
      template:                'template:common:image:item.mediacard',
      inverted:                false,
      displayConditions:       {},
      target:                  '_blank',
    },
    sampleData: {
      moduleTitle:       'Image',
      tooltip:           'Single image',
      moduleDescription: 'Feature an image and caption',
      images:            [
        {
          assetRef: 'asset:image:1200x803:/images/9010/f9dcb669-2ce5-4565-92f1-2e51d5e862db/Caribbean_flamingo.jpg',
          caption:  'This is my caption',
        },
      ],
      template: 'template:common:image:item.mediacard',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.IMAGE_GALLERY]: {
    icon:            enums.icons.GALLERY,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Image Gallery',
      tooltip:           'Layouts for multiple images',
      moduleDescription: 'Feature an image gallery',
      images:            [],
      galleryType:       'none', // TODO: not used by new MEs, check if still needed after removing legacy ME code.
      template:          'template:common:carousel:set.mediaitem.icon',
      target:            '_blank',
    },
  },

  [MODULE_TYPES.MX_IMAGE_GALLERY]: {
    icon:            enums.icons.GALLERY,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Image Gallery',
      tooltip:           'Layouts for multiple images',
      moduleDescription: 'Feature an image gallery',
      images:            [
        { assetRef: '', altText: '', link: '', linkText: '' },
        { assetRef: '', altText: '', link: '', linkText: '' },
      ],
      template:          'template:common:gallery:carousel.mediacard.std',
      target:            '_blank',
      tokenName:         '',
      carouselType:      'thumbnails',
      dotShape:          'circle',
      dotLocation:       'inside',
      autoTransition:    false,
      transitionTime:    '2',
      arrowColor:        '#77d3c6',
      dotColor:          '#77d3c6',
      imagesPerRow:      '2',
      style:             'std',
      displayConditions: {},
      copyTextAlignment: `${enums.positions.verticalAlign.MIDDLE} ${enums.textAlignments.LEFT}`,
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.FORM]: {
    icon:            enums.icons.FORM,
    componentClass:  'content',
    columnPlacement: layoutPlacements.MID_WIDE,
    maxNumPerPage:   1,
    maxNumberError:  'You may have a maximum of one \'Form\' module per experience',
    data:            {
      moduleTitle:       'Form',
      tooltip:           'Entry form selected for this experience',
      moduleDescription: 'Display a preselected form',
      displayProgress:   'none',
      //template:       'template:campaign:form_campaign:left',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.PROMOTION]: {
    icon:              enums.icons.STARBURST,
    componentClass:    'content',
    columnPlacement:   layoutPlacements.ALL,
    maxNumPerCampaign: 1,
    maxNumberError:    'You may have a maximum of one \'Promotions\' module per campaign and only in the content section',
    data:              {
      moduleTitle:         'Promotions',
      tooltip:             'List of promotions (advanced)',
      moduleDescription:   'Promotions',
      template:            'template:campaign:promotions:grid.promotion.l',
      maxItems:            50,
      displayItemTemplate: 'postcard.left',
      sort:                'currentFuturePast',
      target:              '_blank',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.HTML]: {
    icon:            enums.icons.CODE,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'HTML',
      tooltip:           'Custom HTML',
      moduleDescription: 'Add custom HTML code',
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.SOCIAL_CONTENT_WALL]: {
    icon:            enums.icons.SOCIALWALL,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:          'Social Wall',
      tooltip:              'Visualisations for social content/UGC',
      moduleDescription:    'Display social content wall',
      animation:            'fade',
      target:               '_blank',
      render:               'overlay',
      showProfile:          'off',
      scope:                'combined',
      detailTemplate:       'media',
      template:             'template:common:social:grid.medialayer.s',
      ctaDisplayItemRender: 'overlay',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.SOCIAL_CONTENT_CAROUSEL]: {
    icon:            enums.icons.CAROUSEL,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:          'Carousel', // 'Social Carousel'
      tooltip:              'Slideshow of social content/UGC',
      moduleDescription:    'Display social content carousel',
      animation:            'fade',
      target:               '_blank',
      render:               'overlay',
      showProfile:          'off',
      scope:                'combined',
      template:             'template:common:carousel:set.mediacard.thumbnail',
      ctaDisplayItemRender: 'overlay',
      detailTemplate:       'media',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.AVATAR_WALL]: {
    icon:            enums.icons.MOSAIC,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Avatar Wall', // 'Social Mosaic'
      tooltip:           'Avatar grid with animated social content/UGC',
      moduleDescription: 'Display social content mosaic',
      animation:         'fade',
      target:            '_blank',
      render:            'overlay',
      showProfile:       'on',
      scope:             'combined',
      template:          'template:common:gallery:grid.avatar',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.VIDEO]: {
    icon:            enums.icons.VIDEO,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Video',
      tooltip:           'Single video',
      moduleDescription: 'Add a Youtube or Vimeo video URL',
      videos:            [
        {
          assetRef: '',
          caption:  '',
        },
      ],
      target:                  '_blank',
      render:                  'overlay',
      template:                'template:common:video:item.mediacard',
      useShare:                'nosharing',
      shareStyle:              'icon',
      sharingConfig:           defaultSharingConfig,
      backgroundImageAssetRef: '',
      inverted:                false,
      displayConditions:       {},
    },
    sampleData: {
      moduleTitle:       'Video',
      tooltip:           'Single video',
      moduleDescription: 'Add a Youtube or Vimeo video URL',
      videos:            [
        {
          caption:   'This is my caption',
          rawUrl:    'https://www.youtube.com/watch?v=hY7m5jjJ9mM',
          assetRef:  'weburl:video:auto:https://www.youtube.com/watch?v=hY7m5jjJ9mM',
          thumbnail: 'weburl:image:1280x720://img.youtube.com/vi/hY7m5jjJ9mM/0.jpg',
        },
      ],
      target:     '_blank',
      render:     'overlay',
      template:   'template:common:video:item.mediacard',
      useShare:   'nosharing',
      shareStyle: 'icon',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.VIDEO_GALLERY]: {
    icon:            enums.icons.VIDEOGALLERY,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Video Gallery',
      tooltip:           'Layouts for multiple videos',
      moduleDescription: 'Feature a video gallery',
      videos:            [],
      target:            '_blank',
      render:            'overlay',
      template:          'template:common:carousel:set.mediaitem.icon',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.LINK]: {
    icon:            enums.icons.CONNECT,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Link',
      tooltip:           'List of links',
      moduleDescription: 'Define a link and associated options',
      links:             [{ title: '' }],
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.COUNTDOWN]: {
    icon:            enums.icons.COUNTDOWN,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Countdown',
      tooltip:           'Countdown to key events',
      moduleDescription: 'Add a countdown timer',
      countdownDate:     defaultCountdownDate.format('YYYY-MM-DD HH:mm:ss'),
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.MX_COUNTDOWN]: {
    icon:            enums.icons.COUNTDOWN,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Countdown',
      tooltip:           'Countdown to key events',
      moduleDescription: 'Add a countdown timer',
      countdownDate:     defaultCountdownDate.format('YYYY-MM-DD HH:mm:ss'),
      displayConditions: {},
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.SOCIAL_PLUGIN]: {
    icon:            enums.icons.SOCIALPLUGIN,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Social Plugin',
      tooltip:           'Facebook Like, Share and Comment plugins',
      moduleDescription: 'Feature Facebook social widgets',
      showFaces:         'off',
      pluginType:        'pluginTypeComment',
      colourScheme:      'light',
      fbLikeVerb:        'like',
      fbLikeStyle:       '',
      commentCount:      10,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.TILT]: {
    // DEPRECATED
    icon:            enums.icons.CHART,
    componentClass:  'content',
    columnPlacement: layoutPlacements.MID_WIDE,
    data:            {
      moduleTitle:       'Tilt',
      tooltip:           'Visualize the conversation around two or more topics on X or Instagram',
      moduleDescription: 'Visualize the conversation around two or more topics on X or Instagram',
      feeds:             [],
      chartHeight:       '500',
      template:          'vertical',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.MOMENTS]: {
    // DEPRECATED
    icon:            enums.icons.INSIGHTS,
    componentClass:  'content',
    columnPlacement: layoutPlacements.MID_WIDE,
    data:            {
      moduleTitle:       'Moments',
      tooltip:           'Time series display of social conversation',
      moduleDescription: 'Display a moments visual',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.ENTRIES]: {
    icon:            enums.icons.EMAIL,
    componentClass:  'content',
    columnPlacement: layoutPlacements.FULL,
    data:            {
      moduleTitle:          'Entries',
      tooltip:              'Layouts for UGC entries with voting and filtering',
      moduleDescription:    'Display content created by users',
      enableSort:           'on',
      enableVoting:         'on',
      showVoteCount:        'on',
      sort:                 'random',
      show:                 'recent',
      render:               'overlay',
      detailTemplate:       'media',
      showMoreMode:         'mode.showmore.append',
      template:             'template:campaign:entries:list.mediacard',
      ctaDisplayItemRender: 'overlay',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.VOTING]: {
    icon:            enums.icons.VOTING,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Voting',
      tooltip:           'Head-to-head voting on UGC entries',
      moduleDescription: 'Display vote counts',
      showVoteCount:     true,
      enableVoting:      'on',
      //template:       'template:campaign:voting:list.mediacard',
      maxItems:          2,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.SHARING]: {
    icon:            enums.icons.SHARE2,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Sharing',
      tooltip:           'Share to social networks',
      moduleDescription: 'Define sharing content and options',
      useShare:          'campaign',
      shareStyle:        'icon',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.MX_SHARING]: {
    icon:            enums.icons.SHARE2,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Sharing',
      tooltip:           'Share to social networks',
      moduleDescription: 'Define sharing content and design',
      shareStyle:        'icon',
      copyTextAlignment: `${enums.positions.verticalAlign.MIDDLE} ${enums.textAlignments.CENTER}`,
      style:             'std',
      sharingConfig:     {
        [sharingNetworks.DEFAULT]: {
          data: {
            [sharingFields.LINK]: sharingUrlValues.EXPERIENCE_URL,
          },
        },
      },
      displayConditions: {},
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.SCORE_RESULT]: {
    icon:            enums.icons.QUIZ_RESULT,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Quiz Results',
      tooltip:           'Quiz score or category with related result information',
      moduleDescription: 'Display user’s quiz outcomes based on their responses',
      //template:       'template:campaign:scoreresults:item.result',
    },
  },

  [MODULE_TYPES.MX_RESULT]: {
    icon:            enums.icons.QUIZ_RESULT,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    maxNumPerPage:   1,
    maxNumberError:  'You may have a maximum of one \'Score Result \' module and only on the confirmation page',
    data:            {
      moduleTitle:           'Quiz Results',
      tooltip:               'Quiz score related result information',
      moduleDescription:     'Display user’s quiz outcomes based on their responses',
      template:              'template:common:rewards:item.postcard.bottom',
      multipleResultsLayout: MULTIPLE_RESULTS_LAYOUT_TYPE.STACK,
      copyTextAlignment:     'middle center',
      style:                 'std',
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.LEADERBOARD]: {
    icon:            enums.icons.RANKINGS,
    componentClass:  'content',
    columnPlacement: layoutPlacements.MID_WIDE,
    data:            {
      moduleTitle:       'Leaderboard',
      tooltip:           'Quiz leaderboard based on score',
      moduleDescription: 'Display a leaderboard',
      maxItems:          10,
      //template:       'template:campaign:leaderboard:list.result',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.CAMPAIGN_HEADER]: {
    // In use, but not used in page designer module list
    icon:            enums.icons.TEXT,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Page Header',
      tooltip:           '',
      moduleDescription: 'Page Header',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.MY_REWARDS]: {
    icon:            enums.icons.PROMOTION,
    componentClass:  'content',
    columnPlacement: layoutPlacements.FULL,
    data:            {
      moduleTitle:       'My Rewards',
      tooltip:           'Coupon or reward',
      moduleDescription: 'Display user’s rewards and achievements',
      //template:       'template:campaign:myrewards:list.reward.inline',
      noReward:          {
        heading:     'Unlucky.',
        description: 'Sorry, you did not receive a reward this time.',
      },
      rewardListStrategy: 'strategy.rewardlist.current',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.MX_MY_REWARDS]: {
    icon:            enums.icons.PROMOTION,
    componentClass:  'content',
    columnPlacement: layoutPlacements.FULL,
    data:            {
      moduleTitle:           'My Rewards',
      tooltip:               'Coupon or reward',
      moduleDescription:     'Display user’s rewards and achievements',
      template:              'template:common:rewards:item.postcard.bottom',
      multipleResultsLayout: MULTIPLE_RESULTS_LAYOUT_TYPE.CAROUSEL,
      copyTextAlignment:     'middle center',
      style:                 'std',
      target:                '_blank',
      scratchCardEnabled:    false,
      scratchCardPosition:   'center',
      scratchCardShape:      'rectangle',
      scratchCardWidth:      '400',
      scratchCardHeight:     '400',
      displayConditions:     {},
      reward:                {
        target: '_blank',
      },
      noReward: {
        target: '_blank',
      },
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.GOAL]: {
    icon:            enums.icons.TARGET2,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Goal',
      tooltip:           'Call-to-action with progress towards milestones and final goal',
      moduleDescription: 'Allow users to particpate in target goals and milestones',
      template:          'template:campaign:goal:horizontal',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [MODULE_TYPES.PARTICIPANT_WALL]: {
    // DEPRECATED
    icon:            enums.icons.AUDIENCE,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       'Participants',
      tooltip:           'Avatar grid showing entries from social networks',
      moduleDescription: 'Feature participants in a social wall',
      maxItems:          24,
      target:            '_blank',
    },
  },

  //---------------------------------------------- form fields
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.TEXT]: {
    icon:            enums.icons.TEXT,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Text'),
      tooltip:           '',
      moduleDescription: 'Free form text field',
      required:          false,
      label:             'Enter text',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         20,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      placeholder:       '',
      displayAsPassword: false,
      matchRules:        '',
      displayConditions: {},
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.PARAGRAPH]: {
    icon:            enums.icons.BULLET,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Paragraph'),
      tooltip:           '',
      moduleDescription: 'Paragraph field',
      label:             'Enter text',
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      responsiveHeight:  RESPONSIVE_SIZES.MEDIUM,
      placeholder:       '',
      required:          false,
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      height:            100,
      minPxSize:         20,
      maxHeightPx:       250,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.NUMBER]: {
    icon:            enums.icons.NUMBER,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Number'),
      tooltip:           '',
      moduleDescription: 'Number field',
      required:          false,
      label:             'Number',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         20,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      maxLengthUnit:     MAX_LENGTH_UNITS.VALUE, // using MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE!!! Text fields are using MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      placeholder:       '',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.CHECKBOX]: {
    icon:            enums.icons.CHECK_SELECTED,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       'Multiple Choice',
      displayConditions: {},
      tooltip:           '',
      moduleDescription: 'Customizable multiple select field',
      label:             'Select a Choice',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CHECKBOX,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      required:          false,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 1', id: 'option_1', remoteId: '1' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      style:                      'std',
      sizing:                     FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:             RESPONSIVE_SIZES.MEDIUM,
      progressiveMode:            false,
      validationMode:             VALIDATION_MODE.STRICT,
      filterScope:                PROGRESSIVE_FILTER_SCOPE.CAMPAIGN,
      filterByDays:               false,
      filterByDaysSkipFirstVisit: false,
      limitResponses:             false,
      minimumResponses:           undefined,
      maximumResponses:           undefined,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.MULTI_OPTIN]: {
    icon:                 enums.icons.RULES,
    componentClass:       'form',
    columnPlacement:      layoutPlacements.ALL,
    previewSize:          enums.widths.SIXTEEN,
    addRestrictedError:   'Only admins can add multi opt-in fields',
    cloneRestrictedError: 'Only admins can clone multi opt-in fields',
    editRestrictedError:  'Only admins can edit multi opt-in fields',
    data:                 {
      moduleTitle:       locale('Multi Opt-in'),
      displayConditions: {},
      tooltip:           '',
      moduleDescription: 'Checkbox field for multiple opt-ins',
      required:          false,
      label:             'Select a Choice',
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CHECKBOX,
      imageFit:          IMAGE_FITS.CONTENT,
      profile:           MULTI_OPT_IN_SCOPE.BRAND,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:     'Option 1',
          id:        'option_1',
          remoteId:  '1',
          daysShown: DAYS_ALL_STR,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:     'Option 2',
          id:        'option_2',
          remoteId:  '2',
          daysShown: DAYS_ALL_STR,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:     'Option 3',
          id:        'option_3',
          remoteId:  '3',
          daysShown: DAYS_ALL_STR,
        }),
      ],
      displaySelectAll:           false,
      progressiveMode:            false,
      validationMode:             VALIDATION_MODE.STRICT,
      filterScope:                PROGRESSIVE_FILTER_SCOPE.ACCOUNT,
      filterByDays:               false,
      filterByDaysSkipFirstVisit: false,
      sizing:                     FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:             RESPONSIVE_SIZES.MEDIUM,
      autoNavOnClick:             true,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [FIELD_TYPES.RATING]: {
    icon:            enums.icons.RANKINGS,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      displayConditions: {},
      ratingBylineList:  ['Bad', 'Average', 'Excellent'],
      moduleTitle:       'Rating Scale',
      required:          false,
      tooltip:           '',
      moduleDescription: 'Rating Scale Description',
      label:             'Select a Choice',
      profile:           FIELD_PROFILES.NONE,
      tileStyle:         TILE_STYLES.RATING_STAR,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      iconSet:           ratingIcons[0].value,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    '1',
          id:       'option_1',
          remoteId: '1',
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    '2',
          id:       'option_2',
          remoteId: '2',
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    '3',
          id:       'option_3',
          remoteId: '3',
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    '4',
          id:       'option_4',
          remoteId: '4',
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    '5',
          id:       'option_5',
          remoteId: '5',
        }),
      ],
      style:           'std',
      sizing:          FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:  undefined, //this needs to be set, so that the module inject the key in state for us to update based on context (it should ONLY have value when we have TILE_STYLES.RATING_SLIDER)
      progressiveMode: false,
      validationMode:  VALIDATION_MODE.STRICT,
      filterScope:     PROGRESSIVE_FILTER_SCOPE.CAMPAIGN,
      autoNavOnClick:  false,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.RADIO]: {
    icon:            enums.icons.RADIO_SELECTED,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      displayConditions: {},
      moduleTitle:       'Single Choice',
      required:          false,
      tooltip:           '',
      moduleDescription: 'Customizable single select field',
      label:             'Select a Choice',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.GRID,
      tileStyle:         TILE_STYLES.CARD,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 1', id: 'option_1', remoteId: '1' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      style:                      'std',
      sizing:                     FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:             RESPONSIVE_SIZES.MEDIUM,
      progressiveMode:            false,
      validationMode:             VALIDATION_MODE.STRICT,
      filterScope:                PROGRESSIVE_FILTER_SCOPE.CAMPAIGN,
      filterByDays:               false,
      filterByDaysSkipFirstVisit: false,
      autoNavOnClick:             true,
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.POLL]: {
    icon:            enums.icons.INSIGHTS,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Classic Poll'),
      required:          true,
      tooltip:           '',
      moduleDescription: 'Classic Poll field',
      label:             'Select a Choice',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CIRCLE,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 1', id: 'option_1', remoteId: '1' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      style:             'std',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.MX_POLL]: {
    icon:                  enums.icons.CHART,
    componentClass:        'form',
    columnPlacement:       layoutPlacements.ALL,
    previewSize:           enums.widths.SIXTEEN,
    addedToContentWarning: 'Polls in the Content Section will record anonymous votes',
    addedToContentWarningTooltip:
      'This poll has been placed in the Content Section of the experience. When a user votes, it will record an anonymous vote only. If you have a form elsewhere in the experience and want to record which answer the user gave as part of a form submission, you should place the poll within the Form Section.',
    data: {
      moduleTitle:       'Poll',
      required:          true,
      tooltip:           '',
      moduleDescription: 'Ask users to vote',
      heading:           'Select a Choice',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.RT_HORIZONTAL,
      tileStyle:         TILE_STYLES.CIRCLE,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 1', id: 'option_1', remoteId: '1' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      style:                  'std',
      sizing:                 FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:         RESPONSIVE_SIZES.MEDIUM,
      fraudDetectionStrategy: RefData.Enum.STRATEGY.FRAUD.NONE,
      displayType:            MX_POLL_DISPLAY.PERCENTAGE,
      autoAdvance:            AUTO_ADVANCE.OFF.submitValue,
      displayConditions:      {},
      columns:                undefined,
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.SELECT]: {
    icon:            enums.icons.SWITCH,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('DropDown'),
      displayConditions: {},
      tooltip:           '',
      moduleDescription: 'Customizable single select field',
      label:             'Select a Choice',
      required:          false,
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    'Option 1',
          id:       'option_1',
          remoteId: '1',
          selected: true,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      selectionLabel:             '',
      sizing:                     FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:             RESPONSIVE_SIZES.MEDIUM,
      size:                       100,
      minPxSize:                  30,
      progressiveMode:            false,
      validationMode:             VALIDATION_MODE.STRICT,
      filterScope:                PROGRESSIVE_FILTER_SCOPE.CAMPAIGN,
      filterByDays:               false,
      filterByDaysSkipFirstVisit: false,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.FILTER]: {
    icon:            enums.icons.CHART,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   3,
    maxNumberError:
      'You may have a maximum of three \'filters\' per page. Only one filter field is allowed per filter scope.',
    restrictedOnFormTypeError: 'This field is restricted to Contest forms only.',
    data:                      {
      moduleTitle:       locale('Filter'),
      tooltip:           '',
      moduleDescription: 'Filter field',
      label:             'Select a Filter',
      required:          false,
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      filterScope:       FILTER_SCOPE.FIRST,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    'Option 1',
          id:       'option_1',
          remoteId: '1',
          selected: true,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      selectionLabel:    '',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.NAME]: {
    icon:            enums.icons.TEXT,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   1, //as per the comments in WYN-5343, we are restricting the number of 'name' fields a user can add to a page
    maxNumberError:  'You may have a maximum of one \'Name\' module per form and only in the form section',
    data:            {
      moduleTitle:       'Name',
      tooltip:           '',
      moduleDescription: 'Name field',
      format:            NAME_FORMATS.NO_TITLE_F,
      firstName:         'First Name',
      lastName:          'Last Name',
      title:             'Title',
      required:          false,
      label:             'Name',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         150,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      profile:           FIELD_PROFILES.NAME,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.ADDRESS]: {
    icon:            enums.icons.LOCATION,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      address1:          '',
      address2:          '',
      city:              '',
      country:           COUNTRY.UNITED_KINGDOM,
      format:            ADDRESS_FORMAT.UK,
      includeCountry:    true,
      label:             'Address',
      moduleDescription: 'Address field',
      moduleTitle:       locale('Address'),
      profile:           FIELD_PROFILES.ADDRESS,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      required:          false,
      state:             '', //state/province/region
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         200,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      validationType:    VALIDATION_TYPE.COMPOUND,
      zip:               '',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.EMAIL]: {
    icon:            enums.icons.EMAIL,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:         locale('Email'),
      tooltip:             '',
      moduleDescription:   'Unique identifier for an entrant',
      required:            false,
      label:               'Email',
      placeholder:         'me@email.com',
      reEnterLabel:        'Re-enter Email', // displayed when double validation is true
      emailValidationType: EMAIL_VALIDATION_TYPES.SINGLE,
      sizing:              FIELD_SIZINGS.RESPONSIVE,
      size:                100,
      minPxSize:           20,
      responsiveSize:      RESPONSIVE_SIZES.MEDIUM,
      profile:             FIELD_PROFILES.EMAIL,
      showFieldTo:         SHOW_FIELDS_TO.EVERYONE,
      displayConditions:   {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.DATE]: {
    icon:            enums.icons.CALENDAR,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Date'),
      tooltip:           '',
      moduleDescription: 'Date field',
      required:          false,
      label:             'Date',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      profile:           FIELD_PROFILES.DATE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      minAgeComparison:  AGE_COMPARISON.CURRENT_DATE,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.DATE_OF_BIRTH]: {
    icon:            enums.icons.CALENDAR,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:              locale('Date of Birth'),
      tooltip:                  '',
      moduleDescription:        'DOB field with different date formats',
      dateFormat:               DATE_FORMATS.D_M_Y,
      minAge:                   '',
      minAgeComparison:         AGE_COMPARISON.CURRENT_DATE,
      coppaEnabled:             false,
      responsiveSize:           RESPONSIVE_SIZES.MEDIUM,
      label:                    locale('Date of Birth'),
      required:                 false,
      sizing:                   FIELD_SIZINGS.RESPONSIVE,
      size:                     100,
      minPxSize:                80,
      profile:                  FIELD_PROFILES.DATE,
      showFieldTo:              SHOW_FIELDS_TO.EVERYONE,
      remoteFieldChangedByUser: false,
    },
  },

  [FIELD_TYPES.MX_DATE]: {
    icon:            enums.icons.CALENDAR,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:              'Date',
      tooltip:                  '',
      moduleDescription:        'Date field',
      dateFormat:               DATE_FORMATS.D_M,
      responsiveSize:           RESPONSIVE_SIZES.MEDIUM,
      label:                    'Enter a Date',
      required:                 false,
      sizing:                   FIELD_SIZINGS.RESPONSIVE,
      size:                     100,
      minPxSize:                80,
      remoteFieldChangedByUser: false,
      displayConditions:        {},
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.TIME]: {
    icon:            enums.icons.COUNTDOWN,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       locale('Time'),
      tooltip:           '',
      moduleDescription: 'Time field',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.PHONE]: {
    icon:            enums.icons.PHONE,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Phone'),
      tooltip:           '',
      moduleDescription: 'Phone field',
      label:             'Enter phone number',
      phoneFormat:       PHONE_FORMAT.UK,
      placeholder:       '',
      required:          false,
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         150,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.WEBSITE]: {
    icon:            enums.icons.WEB,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Website'),
      tooltip:           '',
      moduleDescription: 'Website field',
      required:          false,
      label:             'Website',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         20,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      placeholder:       'https://',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.COUNTRY]: {
    icon:            enums.icons.MAP,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Country'),
      tooltip:           '',
      moduleDescription: 'Country field',
      required:          false,
      label:             'Country',
      country:           COUNTRY.DEFAULT,
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         20,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.INFO]: {
    icon:            enums.icons.INFO,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Information'),
      tooltip:           '',
      moduleDescription: 'Info field',
      required:          false,
      label:             'Enter text',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      responsiveSize:    RESPONSIVE_SIZES.EXTRA_LARGE,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      placeholder:       '',
      text:              '',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.BREAK]: {
    icon:            enums.icons.CONNECT,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       locale('Break'),
      tooltip:           '',
      moduleDescription: 'Break field',
      type:              BREAK_TYPE.PAGE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.GROUP]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    maxNumPerPage:   1,
    maxNumberError:  locale('You may have a maximum of one \'Group\' module per form and only in the form section'),
    data:            {
      moduleTitle:       locale('Group'),
      tooltip:           '',
      moduleDescription: 'Group field',

      title:                  'Question Group',
      order:                  'manual',
      recordUserAnswers:      'answer_and_score',
      lives:                  '',
      countdown:              '',
      messagePlayAgain:       'Unlucky! Play again.',
      messageOutOfTime:       'Sorry, you are out of time!',
      messageIncorrectAnswer: 'Ooops! That\'s not the right answer.',
      messageNoLivesLeft:     'You\'re done. No lives left!',
      showFieldTo:            SHOW_FIELDS_TO.EVERYONE,
      skipGroup:              'never',
      groupItems:             [],
      randomCount:            '',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.UPLOAD]: {
    icon:            enums.icons.UPLOAD,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   1,
    maxNumberError:  'You may have a maximum of one \'File Upload\' module per form and only in the form section',
    data:            {
      moduleTitle:       'File Upload',
      tooltip:           '',
      moduleDescription: 'File Upload field',

      required:          false,
      label:             'Upload',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         100,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      placeholder:       '',
      fieldType:         UPLOAD_FIELD_TYPE.IMAGE,
      helpText:          'Maximum file size is 5MB',
      tagline:           '',
      byline:            '',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.USER_PROFILE_UPLOAD]: {
    icon:            enums.icons.USER,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   1,
    maxNumberError:  locale(
      'You may have a maximum of one \'Profile Upload\' module per form and only in the form section'
    ),
    data: {
      moduleTitle:       locale('Profile Upload'),
      tooltip:           '',
      moduleDescription: 'Profile Upload field',

      required:          false,
      label:             'Profile Upload',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      minPxSize:         100,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      placeholder:       '',
      helpText:          'Maximum file size is 5MB',
      tagline:           '',
      byline:            '',
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.USER_COMMENT]: {
    icon:            enums.icons.COMMENT,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   1,
    maxNumberError:  'You may have a maximum of one \'User Comment\' module per form and only in the form section',
    data:            {
      moduleTitle:       locale('User Comment'),
      tooltip:           '',
      moduleDescription: 'User Comment field',
      required:          false,
      placeholder:       'Comment',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      height:            100,
      minPxSize:         20,
      maxHeightPx:       250,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      responsiveHeight:  RESPONSIVE_SIZES.MEDIUM,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      displayConditions: {},
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  /*[FIELD_TYPES.HTML]:                 {
    icon:            enums.icons.CODE,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       locale('Html'),
      tooltip:           '',
      moduleDescription: 'Html field',
    },
  },*/
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.MARKETING]: {
    icon:            enums.icons.CTA,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   3,
    maxNumberError:
      'You may have a maximum of three \'opt-ins\' per page. Each opt-in scope may exist only once on a given form.',
    addRestrictedError:   'Only admins can add opt-in fields',
    cloneRestrictedError: 'Only admins can clone opt-in fields',
    editRestrictedError:  'Only admins can edit opt-in fields',
    data:                 {
      moduleTitle:       locale('Opt-in'),
      tooltip:           '',
      moduleDescription: 'Opt-in field',
      label:             'Please keep me informed of the latest news and offers via email.',
      consent:           OPT_IN_CONSENT.OPT_IN,
      required:          false,
      profile:           FIELD_PROFILES.MARKETING_OPT_IN,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      marketingScope:    OPT_IN_SCOPE.BRAND,
      selected:          false,
      optinPresentation: OPT_IN_PRESENTATION.CHECKBOX,
      acceptanceLabel:   'Accept',
      skipLabel:         'Skip',
      displayConditions: {},
      presentationType:  RefData.Enum.TYPE.POLICY.CHECKBOX,
    },
    sampleData: {
      label:             'Please keep me informed of the latest news and offers via email.',
      moduleTitle:       locale('Opt-in'),
      tooltip:           '',
      moduleDescription: 'Opt-in field',
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.TERMS]: {
    icon:            enums.icons.INFO,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    maxNumPerPage:   1,
    maxNumberError:  'You may have a maximum of one \'T\'s & C\'s\' module per form and only in the form section',
    data:            {
      moduleTitle:        locale('T\'s & C\'s'),
      tooltip:            '',
      moduleDescription:  'Terms & Conditions field',
      selected:           false,
      display:            TERM_DISPLAY.NO_EMBED,
      termsAndConditions: 'I accept the Terms and Conditions.',
      url:                '',
      required:           true,
    },
  },
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.CHALLENGE_QUESTION]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       'Challenge Question',
      tooltip:           '',
      moduleDescription: 'Customizable single select field',
      required:          true,
      label:             'Enter your challenge question here.',
      description:       '',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CIRCLE,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceData), {
          label:    'Option 1',
          id:       'option_1',
          remoteId: '1',
          selected: true,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 2', id: 'option_2', remoteId: '2' }),
        Object.assign(_.cloneDeep(MultipleChoiceData), { label: 'Option 3', id: 'option_3', remoteId: '3' }),
      ],
      style:             'std',
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      autoNavOnClick:    true,
      displayConditions: {},
    },
  }, //quiz with radio buttons
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.QUIZ_SINGLE_ANSWER]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      displayConditions: {},
      moduleTitle:       'Quiz Single Choice',
      required:          true,
      blockAnswers:      false,
      tooltip:           '',
      moduleDescription: 'Single Answer Question field',
      label:             'Select an Answer',
      description:       '',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CIRCLE,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceScoreQuizData), {
          label:    'Option 1',
          id:       'option_1',
          remoteId: '1',
          score:    0,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceScoreQuizData), {
          label:    'Option 2',
          id:       'option_2',
          remoteId: '2',
          score:    0,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceScoreQuizData), {
          label:    'Option 3',
          id:       'option_3',
          remoteId: '3',
          score:    0,
        }),
      ],
      style:          'std',
      sizing:         FIELD_SIZINGS.RESPONSIVE,
      responsiveSize: RESPONSIVE_SIZES.MEDIUM,
    },
  }, //quiz with radio buttons
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.QUIZ_IMAGE_ANSWER]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    data:            {
      moduleTitle:       locale('Image Answer Question'),
      tooltip:           '',
      moduleDescription: 'Image Answer Question field',
    },
  }, //quiz with images...this is DEPRECATED
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.QUIZ_MULTIPLE_ANSWER]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:       'Quiz Multiple Choice',
      displayConditions: {},
      tooltip:           '',
      moduleDescription: 'Multiple Answer Question field',
      required:          true,
      blockAnswers:      false,
      label:             'Select an Answer',
      description:       '',
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CHECKBOX,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      values:            [
        Object.assign(_.cloneDeep(MultipleChoiceScoreQuizData), {
          label:    'Option 1',
          id:       'option_1',
          remoteId: '1',
          score:    0,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceScoreQuizData), {
          label:    'Option 2',
          id:       'option_2',
          remoteId: '2',
          score:    0,
        }),
        Object.assign(_.cloneDeep(MultipleChoiceScoreQuizData), {
          label:    'Option 3',
          id:       'option_3',
          remoteId: '3',
          score:    0,
        }),
      ],
      style:            'std',
      sizing:           FIELD_SIZINGS.RESPONSIVE,
      responsiveSize:   RESPONSIVE_SIZES.MEDIUM,
      limitResponses:   false,
      minimumResponses: undefined,
      maximumResponses: undefined,
    },
  }, //quiz with check boxes
  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md

  [FIELD_TYPES.QUIZ_TEXT]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:        'Short Text',
      tooltip:            '',
      moduleDescription:  'Text Answer Question field',
      responsiveSize:     RESPONSIVE_SIZES.MEDIUM,
      placeholder:        '',
      label:              'Enter question',
      description:        '',
      quizAnswer:         '',
      score:              0,
      rightAnswerMessage: '',
      wrongAnswerMessage: '',
      required:           true,
      blockAnswers:       false,
      sizing:             FIELD_SIZINGS.RESPONSIVE,
      size:               100,
      maxLengthUnit:      MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:            FIELD_PROFILES.NONE,
      showFieldTo:        SHOW_FIELDS_TO.EVERYONE,
      displayConditions:  {},
    },
  }, //quiz with text entry

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [FIELD_TYPES.QUIZ_CATEGORY]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      moduleTitle:        locale('Category Question'),
      tooltip:            '',
      moduleDescription:  'Category Question field',
      responsiveSize:     RESPONSIVE_SIZES.MEDIUM,
      placeholder:        '',
      label:              'Enter your quiz category question here',
      description:        '',
      quizAnswer:         '',
      score:              0,
      rightAnswerMessage: '',
      wrongAnswerMessage: '',
      required:           true,
      sizing:             FIELD_SIZINGS.RESPONSIVE,
      size:               100,
      maxLengthUnit:      MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:            FIELD_PROFILES.NONE,
      showFieldTo:        SHOW_FIELDS_TO.EVERYONE,
      values:             [
        Object.assign(_.cloneDeep(QuizCategoryResultData), { label: 'Option 1', remoteId: 'option_1', selected: true }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), { label: 'Option 2', remoteId: 'option_2' }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), { label: 'Option 3', remoteId: 'option_3' }),
      ],
      displayConditions: {},
    },
  }, //quiz with check boxes

  [FIELD_TYPES.SPIN_THE_WHEEL_GAME]: {
    icon:            enums.icons.SPIN_THE_WHEEL,
    componentClass:  'content',
    columnPlacement: layoutPlacements.ALL,
    moduleGroup:     MODULE_CATEGORIES.GAME, //this will mark the module as part of a group, where extra restrictions can be happening
    data:            {
      moduleTitle:       'Spin the wheel',
      tooltip:           'Spin the wheel game',
      moduleDescription: 'Spin the wheel game',
      pinSize:           enums.sizes.MEDIUM,
      pinPosition:       enums.positions.TOP,
      pinColor:          '#d4d9d9',
      spinTime:          '5',
      spinSpeed:         spinSpeed.SLOW,
      pauseTime:         '1',
      soundEnabled:      false,
      slices:            [
        {
          text:            'Sector 1',
          iconRef:         '',
          color:           '#ffffff',
          backgroundColor: '#6c7373',
          reward:          null,
        },
        {
          text:            'Sector 2',
          iconRef:         '',
          color:           '#ffffff',
          backgroundColor: '#2b8679',
          reward:          null,
        },
      ],
      spinTheWheelBackgroundImageAssetRef: '',
    },
  },

  [FIELD_TYPES.POLICY]: {
    icon:            enums.icons.SECURITY,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    maxNumPerPage:   1,
    maxNumberError:  'You can only have one \'Policy & Rules\' module in a form',
    data:            {
      moduleTitle:       'Policy & Rules',
      tooltip:           'Policy & Rules',
      moduleDescription: 'I have read and agree to the Rules.',
      policy:            '',
      terms:             { url: '' },
      privacy:           { url: '' },
      rules:             { text: '', type: 'url', url: '' },
      presentationType:  RefData.Enum.TYPE.POLICY.CHECKBOX,
    },
  },

  [FIELD_TYPES.SUBMIT]: {
    icon:            enums.icons.CAMPAIGN,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    maxNumPerPage:   1,
    maxNumberError:  'You may have a maximum of one \'Submit\' module per form and only in the form section',
    preventDeletion: true, //this will remove the 'x' remove icon from the module and will also prevent users to delete a row that contains this module
    data:            {
      moduleTitle:           'Submit',
      tooltip:               'Form submit button',
      moduleDescription:     'Form submit button',
      label:                 'Enter',
      errorMessage:          '',
      customizeErrorMessage: false,
    },
  },

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [FIELD_TYPES.MX_QUIZ_CATEGORY_SINGLE]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      displayConditions: {},
      moduleTitle:       'Quiz Single Choice',
      tooltip:           '',
      moduleDescription: 'Single Answer Question',
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      placeholder:       '',
      label:             'Select a Choice',
      description:       '',
      quizAnswer:        '',
      required:          true,
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CIRCLE,
      imageFit:          IMAGE_FITS.EXPAND,

      adobeAttributeId: '', //used only for adobe commerce experiences

      values: [
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 1',
          remoteId:             'option_1',
          id:                   'option_1',
          categoryResultIdList: [],
        }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 2',
          remoteId:             'option_2',
          id:                   'option_2',
          categoryResultIdList: [],
        }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 3',
          remoteId:             'option_3',
          id:                   'option_3',
          categoryResultIdList: [],
        }),
      ],
    },
  }, //single answer category question

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [FIELD_TYPES.QUIZ_SWIPE_SINGLE]: {
    icon:            enums.icons.SWIPE,
    componentClass:  'form',
    columnPlacement: layoutPlacements.FULL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      displayConditions: {},
      moduleTitle:       'Swipe Single Choice',
      tooltip:           '',
      moduleDescription: 'Swipe Single Choice',
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      placeholder:       '',
      label:             'Enter your question here',
      description:       '',
      required:          true,
      profile:           FIELD_PROFILES.NONE,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,
      template:          FIELD_TEMPLATES.GRID, //while the input is missing for this value, we just delegate it to the display side as it is needed for styling
      tileStyle:         TILE_STYLES.BUTTON,
      imageFit:          IMAGE_FITS.EXPAND,
      values:            [
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 1',
          remoteId:             'option_1',
          id:                   'option_1',
          categoryResultIdList: [],
        }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 2',
          remoteId:             'option_2',
          id:                   'option_2',
          categoryResultIdList: [],
        }),
        //only 2 options will EVER be supported
      ],
    },
  }, //single swipe

  // BEFORE YOU EDIT THIS:
  // If you add any attributes to the "data" object, you also need to ensure they get added to the "pageLayout" in the store repo for the relevant experience types that use this module/field
  // Read the doc here: doc/module-editor/how-to-safely-add-data-attributes.md
  [FIELD_TYPES.MX_QUIZ_CATEGORY_MULTIPLE]: {
    icon:            enums.icons.QUIZ,
    componentClass:  'form',
    columnPlacement: layoutPlacements.ALL,
    previewSize:     enums.widths.SIXTEEN,
    data:            {
      displayConditions: {},
      moduleTitle:       'Quiz Multiple Choice',
      tooltip:           '',
      moduleDescription: 'Multiple Answer Question',
      responsiveSize:    RESPONSIVE_SIZES.MEDIUM,
      placeholder:       '',
      label:             'Select a Choice',
      description:       '',
      quizAnswer:        '',
      required:          true,
      sizing:            FIELD_SIZINGS.RESPONSIVE,
      size:              100,
      maxLengthUnit:     MAX_LENGTH_UNITS.CHARACTERS, // make sure you use the appropriate MAX_LENGTH_UNITS.CHARACTERS for UNIT_TYPES.LENGTH, OR use MAX_LENGTH_UNITS.VALUE for UNIT_TYPES.VALUE
      profile:           FIELD_PROFILES.NONE,
      template:          FIELD_TEMPLATES.VERTICAL,
      tileStyle:         TILE_STYLES.CHECKBOX,
      imageFit:          IMAGE_FITS.CONTENT,
      showFieldTo:       SHOW_FIELDS_TO.EVERYONE,

      adobeAttributeId: '', //used only for adobe commerce experiences
      limitResponses:   false,
      minimumResponses: undefined,
      maximumResponses: undefined,
      values:           [
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 1',
          remoteId:             'option_1',
          id:                   'option_1',
          categoryResultIdList: [],
        }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 2',
          remoteId:             'option_2',
          id:                   'option_2',
          categoryResultIdList: [],
        }),
        Object.assign(_.cloneDeep(QuizCategoryResultData), {
          label:                'Option 3',
          remoteId:             'option_3',
          id:                   'option_3',
          categoryResultIdList: [],
        }),
      ],
    },
  }, //multiple answer quiz category
}; // End moduleData

// Module Categories
const moduleCategories = {
  [MODULE_CATEGORIES.GENERAL]: [
    // all: page, promotion, footer
    MODULE_TYPES.HEADLINE,
    MODULE_TYPES.CALLOUT,
    MODULE_TYPES.IMAGE,
    MODULE_TYPES.VIDEO,
    MODULE_TYPES.IMAGE_GALLERY,
    MODULE_TYPES.VIDEO_GALLERY,
    MODULE_TYPES.COUNTDOWN,
    MODULE_TYPES.LINK,
    MODULE_TYPES.HTML,
    MODULE_TYPES.SHARING, // not footer
    MODULE_TYPES.PROMOTION,
  ],
  [MODULE_CATEGORIES.SOCIAL]: [
    MODULE_TYPES.COLLAGE,
    MODULE_TYPES.SOCIAL_CONTENT_WALL,
    MODULE_TYPES.SOCIAL_CONTENT_CAROUSEL,
    MODULE_TYPES.AVATAR_WALL,
    MODULE_TYPES.SOCIAL_PLUGIN,
    MODULE_TYPES.TILT,
    MODULE_TYPES.MOMENTS,
  ],
  [MODULE_CATEGORIES.PARTICIPATION]: [
    MODULE_TYPES.FORM,
    MODULE_TYPES.ENTRIES,
    MODULE_TYPES.VOTING,
    MODULE_TYPES.GOAL,
    MODULE_TYPES.SCORE_RESULT,
    MODULE_TYPES.LEADERBOARD,
    MODULE_TYPES.POLL_RESULTS,
    MODULE_TYPES.MY_REWARDS,
  ],
};

// ***
const footerModuleCategories = {
  [MODULE_CATEGORIES.CONTENT]: {
    name:    'Content',
    byline:  'Footer components provide a mix of headline, callout, image and custom HTML.',
    modules: [
      { type: MODULE_TYPES.HEADLINE, name: moduleData[MODULE_TYPES.HEADLINE].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.CALLOUT, name: moduleData[MODULE_TYPES.CALLOUT].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.IMAGE, name: moduleData[MODULE_TYPES.IMAGE].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.HTML, name: moduleData[MODULE_TYPES.HTML].data.moduleTitle, appVersion: 75 },
    ],
  },
};

// Clonning footer categories for header as they are the same at the moment (but might be changed in the future)
const headerModuleCategories = _.clone(footerModuleCategories);

const screenModuleCategories = {
  [MODULE_CATEGORIES.CONTENT]: {
    name:    'Content',
    byline:  'Content components provide a mix of text, images, videos and galleries.',
    modules: [
      { type: MODULE_TYPES.HEADLINE, name: moduleData[MODULE_TYPES.HEADLINE].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.CALLOUT, name: moduleData[MODULE_TYPES.CALLOUT].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.IMAGE, name: moduleData[MODULE_TYPES.IMAGE].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.VIDEO, name: moduleData[MODULE_TYPES.VIDEO].data.moduleTitle, appVersion: 75 },
      {
        type:       MODULE_TYPES.MX_IMAGE_GALLERY,
        name:       moduleData[MODULE_TYPES.MX_IMAGE_GALLERY].data.moduleTitle,
        appVersion: 105,
      },
      {
        type:       MODULE_TYPES.VIDEO_GALLERY,
        name:       moduleData[MODULE_TYPES.VIDEO_GALLERY].data.moduleTitle,
        appVersion: 999,
      },
      {
        type:       MODULE_TYPES.MX_COUNTDOWN,
        name:       moduleData[MODULE_TYPES.MX_COUNTDOWN].data.moduleTitle,
        appVersion: 100,
      },
      { type: MODULE_TYPES.LINK, name: moduleData[MODULE_TYPES.LINK].data.moduleTitle, appVersion: 999 },
      { type: MODULE_TYPES.HTML, name: moduleData[MODULE_TYPES.HTML].data.moduleTitle, appVersion: 75 },
      { type: MODULE_TYPES.MX_SHARING, name: moduleData[MODULE_TYPES.MX_SHARING].data.moduleTitle, appVersion: 102 },
      {
        type:                          MODULE_TYPES.PROMOTION,
        name:                          moduleData[MODULE_TYPES.PROMOTION].data.moduleTitle,
        appVersion:                    108,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.REVEAL],
        restrictedToClassRef:          [RefData.Enum.CLASS.DISPLAYOBJECT.CONTENT, RefData.Enum.CLASS.DISPLAYOBJECT.ENTRY],
      },
      { type: FIELD_TYPES.MX_POLL, name: moduleData[FIELD_TYPES.MX_POLL].data.moduleTitle, appVersion: 108 },
    ],
  },
  [MODULE_CATEGORIES.SOCIAL]: {
    name:    'Social',
    byline:  'byline to be updated...',
    modules: [
      {
        type:           MODULE_TYPES.SOCIAL_CONTENT_WALL,
        name:           moduleData[MODULE_TYPES.SOCIAL_CONTENT_WALL].data.moduleTitle,
        appVersion:     999,
        capabilityGate: capabilities.ADVOCACY_PACKAGE,
      },
      {
        type:           MODULE_TYPES.SOCIAL_CONTENT_CAROUSEL,
        name:           moduleData[MODULE_TYPES.SOCIAL_CONTENT_CAROUSEL].data.moduleTitle,
        appVersion:     999,
        capabilityGate: capabilities.ADVOCACY_PACKAGE,
      },
      {
        type:           MODULE_TYPES.AVATAR_WALL,
        name:           moduleData[MODULE_TYPES.AVATAR_WALL].data.moduleTitle,
        appVersion:     999,
        capabilityGate: capabilities.ADVOCACY_PACKAGE,
      },
      {
        type:           MODULE_TYPES.SOCIAL_PLUGIN,
        name:           moduleData[MODULE_TYPES.SOCIAL_PLUGIN].data.moduleTitle,
        appVersion:     999,
        capabilityGate: capabilities.ADVOCACY_PACKAGE,
      },
      {
        type:           MODULE_TYPES.TILT,
        name:           moduleData[MODULE_TYPES.TILT].data.moduleTitle,
        appVersion:     999,
        capabilityGate: capabilities.ADVOCACY_PACKAGE,
      },
    ],
  },

  [MODULE_CATEGORIES.PARTICIPATION]: {
    name:    'Participation',
    byline:  'Components to display user’s rewards, results and achievements',
    modules: [
      { type: MODULE_TYPES.FORM, name: moduleData[MODULE_TYPES.FORM].data.moduleTitle, appVersion: 999 },
      { type: MODULE_TYPES.ENTRIES, name: moduleData[MODULE_TYPES.ENTRIES].data.moduleTitle, appVersion: 999 },
      { type: MODULE_TYPES.VOTING, name: moduleData[MODULE_TYPES.VOTING].data.moduleTitle, appVersion: 999 },
      { type: MODULE_TYPES.GOAL, name: moduleData[MODULE_TYPES.GOAL].data.moduleTitle, appVersion: 999 },
      { type: MODULE_TYPES.LEADERBOARD, name: moduleData[MODULE_TYPES.LEADERBOARD].data.moduleTitle, appVersion: 999 },
      {
        type:       MODULE_TYPES.POLL_RESULTS,
        name:       moduleData[MODULE_TYPES.POLL_RESULTS].data.moduleTitle,
        appVersion: 999,
      },
      {
        type:                 MODULE_TYPES.MX_POLL_RESULTS,
        name:                 moduleData[MODULE_TYPES.MX_POLL_RESULTS].data.moduleTitle,
        appVersion:           108,
        restrictedToClassRef: [RefData.Enum.CLASS.DISPLAYOBJECT.CONFIRMATION],
      },
      {
        type:                 MODULE_TYPES.MX_MY_REWARDS,
        name:                 moduleData[MODULE_TYPES.MX_MY_REWARDS].data.moduleTitle,
        appVersion:           102,
        restrictedToClassRef: [RefData.Enum.CLASS.DISPLAYOBJECT.CONFIRMATION],
      },

      {
        type:                          MODULE_TYPES.MX_RESULT,
        name:                          moduleData[MODULE_TYPES.MX_RESULT].data.moduleTitle,
        appVersion:                    106,
        restrictedToClassRef:          [RefData.Enum.CLASS.DISPLAYOBJECT.CONFIRMATION],
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ],
        restrictedToQuizType:          [quizTypes.SCORE, quizTypes.CATEGORY_INTERSECT, quizTypes.PRODUCT_FEED],
      },
    ],
  },
};

// Field Categories for screen designer
const fieldCategoriesScreenDesigner = {
  [MODULE_CATEGORIES.STANDARD]: {
    name:    'Form',
    byline:  'Popular fields for collecting data and asking questions.',
    modules: [
      { type: FIELD_TYPES.NAME, name: moduleData[FIELD_TYPES.NAME].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.ADDRESS, name: moduleData[FIELD_TYPES.ADDRESS].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.EMAIL, name: moduleData[FIELD_TYPES.EMAIL].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.MX_DATE, name: moduleData[FIELD_TYPES.MX_DATE].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.DATE_OF_BIRTH, name: moduleData[FIELD_TYPES.DATE_OF_BIRTH].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.PHONE, name: moduleData[FIELD_TYPES.PHONE].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.WEBSITE, name: moduleData[FIELD_TYPES.WEBSITE].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.MARKETING, name: moduleData[FIELD_TYPES.MARKETING].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.MULTI_OPTIN, name: moduleData[FIELD_TYPES.MULTI_OPTIN].data.moduleTitle, appVersion: 76 },
      //{type: FIELD_TYPES.HTML, name: FIELD_TYPES.HTML, appVersion: 999},
      { type: FIELD_TYPES.INFO, name: moduleData[FIELD_TYPES.INFO].data.moduleTitle, appVersion: 999 },
      { type: FIELD_TYPES.TERMS, name: moduleData[FIELD_TYPES.TERMS].data.moduleTitle, appVersion: 999 },
      { type: FIELD_TYPES.COUNTRY, name: moduleData[FIELD_TYPES.COUNTRY].data.moduleTitle, appVersion: 76 },
      {
        type:       FIELD_TYPES.CHALLENGE_QUESTION,
        name:       moduleData[FIELD_TYPES.CHALLENGE_QUESTION].data.moduleTitle,
        appVersion: 76,
      },
      { type: FIELD_TYPES.TEXT, name: moduleData[FIELD_TYPES.TEXT].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.PARAGRAPH, name: moduleData[FIELD_TYPES.PARAGRAPH].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.NUMBER, name: moduleData[FIELD_TYPES.NUMBER].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.CHECKBOX, name: moduleData[FIELD_TYPES.CHECKBOX].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.RADIO, name: moduleData[FIELD_TYPES.RADIO].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.RATING, name: moduleData[FIELD_TYPES.RATING].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.SELECT, name: moduleData[FIELD_TYPES.SELECT].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.BREAK, name: moduleData[FIELD_TYPES.BREAK].data.moduleTitle, appVersion: 999 },
      { type: FIELD_TYPES.POLL, name: moduleData[FIELD_TYPES.POLL].data.moduleTitle, appVersion: 999 },
      { type: FIELD_TYPES.MX_POLL, name: moduleData[FIELD_TYPES.MX_POLL].data.moduleTitle, appVersion: 108 },
      {
        type:          FIELD_TYPES.POLICY,
        name:          moduleData[FIELD_TYPES.POLICY].data.moduleTitle,
        appVersion:    107,
        expAppVersion: 107,
      },
      { type: FIELD_TYPES.SUBMIT, name: moduleData[FIELD_TYPES.SUBMIT].data.moduleTitle, appVersion: 999 }, //should never show for regular users, cause we are adding the module in the store definition and we are not allowing the user to delete the module, but still need to show, in case product is using the editor to create a new experience to be used in the store
      { type: FIELD_TYPES.UPLOAD, name: moduleData[FIELD_TYPES.UPLOAD].data.moduleTitle, appVersion: 90 },
    ],
  },
  [MODULE_CATEGORIES.USER]: {
    name:    'User',
    byline:  'Collect content that can be published to social walls, collages and contests.',
    modules: [
      {
        type:       FIELD_TYPES.USER_PROFILE_UPLOAD,
        name:       moduleData[FIELD_TYPES.USER_PROFILE_UPLOAD].data.moduleTitle,
        appVersion: 999,
      },
      { type: FIELD_TYPES.USER_COMMENT, name: moduleData[FIELD_TYPES.USER_COMMENT].data.moduleTitle, appVersion: 999 },
      { type: FIELD_TYPES.FILTER, name: moduleData[FIELD_TYPES.FILTER].data.moduleTitle, appVersion: 999 },
    ],
  },
  [MODULE_CATEGORIES.QUIZ]: {
    name:    'Quiz',
    byline:  'Add quiz questions.',
    modules: [
      {
        type:                 FIELD_TYPES.GROUP,
        name:                 moduleData[FIELD_TYPES.GROUP].data.moduleTitle,
        appVersion:           999,
        restrictedToFormType: [RefData.Enum.TYPE.FORM.QUIZ],
        restrictedToQuizType: [quizTypes.SCORE, quizTypes.CATEGORY_MATCH],
      },
      {
        type:                          FIELD_TYPES.QUIZ_SINGLE_ANSWER,
        name:                          moduleData[FIELD_TYPES.QUIZ_SINGLE_ANSWER].data.moduleTitle,
        appVersion:                    106,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ],
        restrictedToQuizType:          [quizTypes.SCORE],
      },
      {
        type:                          FIELD_TYPES.QUIZ_MULTIPLE_ANSWER,
        name:                          moduleData[FIELD_TYPES.QUIZ_MULTIPLE_ANSWER].data.moduleTitle,
        appVersion:                    106,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ],
        restrictedToQuizType:          [quizTypes.SCORE],
      },
      {
        type:                          FIELD_TYPES.QUIZ_TEXT,
        name:                          moduleData[FIELD_TYPES.QUIZ_TEXT].data.moduleTitle,
        appVersion:                    106,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ],
        restrictedToQuizType:          [quizTypes.SCORE],
      },

      {
        type:                          FIELD_TYPES.MX_QUIZ_CATEGORY_SINGLE,
        name:                          moduleData[FIELD_TYPES.MX_QUIZ_CATEGORY_SINGLE].data.moduleTitle,
        appVersion:                    108,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ],
        restrictedToQuizType:          [quizTypes.CATEGORY_INTERSECT, quizTypes.PRODUCT_FEED],
      },
      {
        type:                          FIELD_TYPES.MX_QUIZ_CATEGORY_MULTIPLE,
        name:                          moduleData[FIELD_TYPES.MX_QUIZ_CATEGORY_MULTIPLE].data.moduleTitle,
        appVersion:                    108,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ], //category and score share the same experience typeRef of quiz, their subTyperef differs, where one is score the other is category
        restrictedToQuizType:          [quizTypes.CATEGORY_INTERSECT, quizTypes.PRODUCT_FEED],
      },

      {
        appVersion:                    108, //this app version is only symbolic , but it cannot be "undefined"; modules with appVersion undefined will be filtered out, please update the filter if you wanna clean this
        type:                          FIELD_TYPES.QUIZ_SWIPE_SINGLE,
        name:                          moduleData[FIELD_TYPES.QUIZ_SWIPE_SINGLE].data.moduleTitle,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ],
        restrictedToQuizType:          [quizTypes.CATEGORY_INTERSECT],
      },
    ],
  },
  [MODULE_CATEGORIES.GAME]: {
    name:    'Game',
    byline:  'Add individual games.',
    modules: [
      {
        type:                          FIELD_TYPES.SPIN_THE_WHEEL_GAME,
        name:                          moduleData[FIELD_TYPES.SPIN_THE_WHEEL_GAME].data.moduleTitle,
        appVersion:                    107,
        restrictedToExperienceTypeRef: [RefData.Enum.TYPE.CAMPAIGNOBJECT.GAME],
      },
    ],
  },
};

// Field Categories for form designer
const fieldCategoriesFormDesigner = {
  [MODULE_CATEGORIES.STANDARD]: {
    name:    'Form',
    byline:  'Popular fields for collecting data and asking questions.',
    modules: [
      { type: FIELD_TYPES.NAME, name: moduleData[FIELD_TYPES.NAME].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.ADDRESS, name: moduleData[FIELD_TYPES.ADDRESS].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.EMAIL, name: moduleData[FIELD_TYPES.EMAIL].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.DATE_OF_BIRTH, name: moduleData[FIELD_TYPES.DATE_OF_BIRTH].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.PHONE, name: moduleData[FIELD_TYPES.PHONE].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.WEBSITE, name: moduleData[FIELD_TYPES.WEBSITE].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.MARKETING, name: moduleData[FIELD_TYPES.MARKETING].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.MULTI_OPTIN, name: moduleData[FIELD_TYPES.MULTI_OPTIN].data.moduleTitle, appVersion: 90 },
      { type: MODULE_TYPES.HTML, name: moduleData[MODULE_TYPES.HTML].data.moduleTitle, appVersion: 90 },
      { type: FIELD_TYPES.INFO, name: moduleData[FIELD_TYPES.INFO].data.moduleTitle, appVersion: 90 },
      { type: FIELD_TYPES.TERMS, name: moduleData[FIELD_TYPES.TERMS].data.moduleTitle, appVersion: 90 },
      { type: FIELD_TYPES.COUNTRY, name: moduleData[FIELD_TYPES.COUNTRY].data.moduleTitle, appVersion: 76 },
      {
        type:       FIELD_TYPES.CHALLENGE_QUESTION,
        name:       moduleData[FIELD_TYPES.CHALLENGE_QUESTION].data.moduleTitle,
        appVersion: 76,
      },
      { type: FIELD_TYPES.TEXT, name: moduleData[FIELD_TYPES.TEXT].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.PARAGRAPH, name: moduleData[FIELD_TYPES.PARAGRAPH].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.NUMBER, name: moduleData[FIELD_TYPES.NUMBER].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.CHECKBOX, name: moduleData[FIELD_TYPES.CHECKBOX].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.RADIO, name: moduleData[FIELD_TYPES.RADIO].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.SELECT, name: moduleData[FIELD_TYPES.SELECT].data.moduleTitle, appVersion: 76 },
      { type: FIELD_TYPES.BREAK, name: moduleData[FIELD_TYPES.BREAK].data.moduleTitle, appVersion: 90 },
      { type: FIELD_TYPES.POLL, name: moduleData[FIELD_TYPES.POLL].data.moduleTitle, appVersion: 90 },
      { type: FIELD_TYPES.UPLOAD, name: moduleData[FIELD_TYPES.UPLOAD].data.moduleTitle, appVersion: 90 },
    ],
  },
  [MODULE_CATEGORIES.USER]: {
    name:    'User',
    byline:  'Collect content that can be published to social walls, collages and contests.',
    modules: [
      {
        type:       FIELD_TYPES.USER_PROFILE_UPLOAD,
        name:       moduleData[FIELD_TYPES.USER_PROFILE_UPLOAD].data.moduleTitle,
        appVersion: 90,
      },
      { type: FIELD_TYPES.USER_COMMENT, name: moduleData[FIELD_TYPES.USER_COMMENT].data.moduleTitle, appVersion: 90 },
      { type: FIELD_TYPES.FILTER, name: moduleData[FIELD_TYPES.FILTER].data.moduleTitle, appVersion: 90 },
    ],
  },
  [MODULE_CATEGORIES.QUIZ]: {
    name: 'Quiz',
    byline:
      'Add individual quiz questions or a group of them (allowing lives, countdowns and other advanced features).',
    modules: [
      {
        type:                 FIELD_TYPES.GROUP,
        name:                 moduleData[FIELD_TYPES.GROUP].data.moduleTitle,
        appVersion:           90,
        restrictedToFormType: [RefData.Enum.TYPE.FORM.QUIZ],
        restrictedToQuizType: [quizTypes.SCORE, quizTypes.CATEGORY_MATCH],
      },
      {
        type:                 FIELD_TYPES.QUIZ_SINGLE_ANSWER,
        name:                 moduleData[FIELD_TYPES.QUIZ_SINGLE_ANSWER].data.moduleTitle,
        appVersion:           90,
        restrictedToFormType: [RefData.Enum.TYPE.FORM.QUIZ],
        restrictedToQuizType: [quizTypes.SCORE],
      },
      {
        type:                 FIELD_TYPES.QUIZ_MULTIPLE_ANSWER,
        name:                 moduleData[FIELD_TYPES.QUIZ_MULTIPLE_ANSWER].data.moduleTitle,
        appVersion:           90,
        restrictedToFormType: [RefData.Enum.TYPE.FORM.QUIZ],
        restrictedToQuizType: [quizTypes.SCORE],
      },
      {
        type:                 FIELD_TYPES.QUIZ_TEXT,
        name:                 moduleData[FIELD_TYPES.QUIZ_TEXT].data.moduleTitle,
        appVersion:           90,
        restrictedToFormType: [RefData.Enum.TYPE.FORM.QUIZ],
        restrictedToQuizType: [quizTypes.SCORE],
      },
      {
        type:                 FIELD_TYPES.QUIZ_CATEGORY,
        name:                 moduleData[FIELD_TYPES.QUIZ_CATEGORY].data.moduleTitle,
        appVersion:           90,
        restrictedToFormType: [RefData.Enum.TYPE.FORM.QUIZ],
        restrictedToQuizType: [quizTypes.CATEGORY_MATCH],
      },
    ],
  },
};

// Field Categories for promotion designer
const fieldCategoriesPromotionFormDesigner = {
  [MODULE_CATEGORIES.CONTENT]: {
    name:    'Content',
    byline:  'Content components provide a mix of text, images, videos and galleries.',
    modules: [
      { type: MODULE_TYPES.HEADLINE, name: moduleData[MODULE_TYPES.HEADLINE].data.moduleTitle, appVersion: 111 },
      { type: MODULE_TYPES.CALLOUT, name: moduleData[MODULE_TYPES.CALLOUT].data.moduleTitle, appVersion: 111 },
      { type: MODULE_TYPES.IMAGE, name: moduleData[MODULE_TYPES.IMAGE].data.moduleTitle, appVersion: 111 },
      { type: MODULE_TYPES.VIDEO, name: moduleData[MODULE_TYPES.VIDEO].data.moduleTitle, appVersion: 111 },
      {
        type:       MODULE_TYPES.MX_IMAGE_GALLERY,
        name:       moduleData[MODULE_TYPES.MX_IMAGE_GALLERY].data.moduleTitle,
        appVersion: 111,
      },
      {
        type:       MODULE_TYPES.MX_COUNTDOWN,
        name:       moduleData[MODULE_TYPES.MX_COUNTDOWN].data.moduleTitle,
        appVersion: 111,
      },
      { type: MODULE_TYPES.HTML, name: moduleData[MODULE_TYPES.HTML].data.moduleTitle, appVersion: 111 },
      { type: MODULE_TYPES.MX_SHARING, name: moduleData[MODULE_TYPES.MX_SHARING].data.moduleTitle, appVersion: 111 },
    ],
  },
  [MODULE_CATEGORIES.STANDARD]: {
    name:    'Form',
    byline:  'Popular fields for collecting data and asking questions.',
    modules: [
      { type: FIELD_TYPES.ADDRESS, name: moduleData[FIELD_TYPES.ADDRESS].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.EMAIL, name: moduleData[FIELD_TYPES.EMAIL].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.MX_DATE, name: moduleData[FIELD_TYPES.MX_DATE].data.moduleTitle, appVersion: 111 },
      {
        type:       FIELD_TYPES.DATE_OF_BIRTH,
        name:       moduleData[FIELD_TYPES.DATE_OF_BIRTH].data.moduleTitle,
        appVersion: 111,
      },
      { type: FIELD_TYPES.PHONE, name: moduleData[FIELD_TYPES.PHONE].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.WEBSITE, name: moduleData[FIELD_TYPES.WEBSITE].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.MULTI_OPTIN, name: moduleData[FIELD_TYPES.MULTI_OPTIN].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.COUNTRY, name: moduleData[FIELD_TYPES.COUNTRY].data.moduleTitle, appVersion: 111 },
      {
        type:       FIELD_TYPES.CHALLENGE_QUESTION,
        name:       moduleData[FIELD_TYPES.CHALLENGE_QUESTION].data.moduleTitle,
        appVersion: 111,
      },
      { type: FIELD_TYPES.TEXT, name: moduleData[FIELD_TYPES.TEXT].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.PARAGRAPH, name: moduleData[FIELD_TYPES.PARAGRAPH].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.NUMBER, name: moduleData[FIELD_TYPES.NUMBER].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.CHECKBOX, name: moduleData[FIELD_TYPES.CHECKBOX].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.RADIO, name: moduleData[FIELD_TYPES.RADIO].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.SELECT, name: moduleData[FIELD_TYPES.SELECT].data.moduleTitle, appVersion: 111 },
      { type: FIELD_TYPES.MX_POLL, name: moduleData[FIELD_TYPES.MX_POLL].data.moduleTitle, appVersion: 111 },
    ],
  },
  [MODULE_CATEGORIES.QUIZ]: {
    name: 'Quiz',
    byline:
      'Add individual quiz questions or a group of them (allowing lives, countdowns and other advanced features).',
    modules: [
      {
        type:       FIELD_TYPES.QUIZ_SINGLE_ANSWER,
        name:       moduleData[FIELD_TYPES.QUIZ_SINGLE_ANSWER].data.moduleTitle,
        appVersion: 111,
      },
      {
        type:       FIELD_TYPES.QUIZ_MULTIPLE_ANSWER,
        name:       moduleData[FIELD_TYPES.QUIZ_MULTIPLE_ANSWER].data.moduleTitle,
        appVersion: 111,
      },
      {
        type:       FIELD_TYPES.QUIZ_TEXT,
        name:       moduleData[FIELD_TYPES.QUIZ_TEXT].data.moduleTitle,
        appVersion: 111,
      },
    ],
  },
};

const _sortByName = (modules) => _.sortBy(modules, (m) => m.name);
const _moduleName = (modules) => _.map(modules, (m) => m.type);
const _sortAndPickFn = _.flow([_sortByName, _moduleName]);

/**
 * Filters the module list by designer type/account app version/experience app version/page type ref/display type ref/quiz type/experience type ref
 * Returns an object of categories, each containing the list of modules per category.
 */
const filterModuleCategories = _.memoize(
  (
    designerType,
    categories,
    accountAppVersion = 1,
    expAppVersion = 1,
    pageTypeRef = '',
    displayClassRef = '',
    quizType = '',
    experienceTypeRef
  ) => {
    const skipFilterByExperience = !designerTypes.isNextGenDesigner(designerType);
    return _.reduce(
      categories,
      (accCategory, category, categoryKey) => {
        const _category = _.clone(category);
        const _filteredModules = _sortAndPickFn(
          _.filter(
            _category.modules,
            (m) =>
              //capability filtering — pass if no capability required, or if the capability is present
              (!m.capabilityGate || CapabilityGate(m.capabilityGate)) &&
              //account app version filtering
              m.appVersion <= accountAppVersion &&
              //skip filtering OR module doesn't specify experience app version filtering OR it meets the requirements
              (skipFilterByExperience || !m.expAppVersion || m.expAppVersion <= expAppVersion) &&
              //skip filtering OR module isn't restricted to experience type OR it meets the requirements
              (skipFilterByExperience ||
                !m.restrictedToExperienceTypeRef ||
                _.includes(m.restrictedToExperienceTypeRef, experienceTypeRef.id)) &&
              //page type ref not given OR module isn't restricted to page type OR it meets the requirements
              (!pageTypeRef || !m.restrictedToFormType || _.includes(m.restrictedToFormType, pageTypeRef)) &&
              //page type ref is not QUIZ OR not in the ScreenDesinger (the page type is just page in the screen designer, so in this edge case we need to capture it via designer type) module isn't restricted to quiz type OR it meets the requirements
              (!(pageTypeRef === RefData.Enum.TYPE.FORM.QUIZ || designerTypes.isScreenDesigner(designerType)) ||
                !m.restrictedToQuizType ||
                _.includes(m.restrictedToQuizType, quizType)) &&
              //display class ref not given OR module isn't restricted to display class OR it meets the requirements
              (!displayClassRef || !m.restrictedToClassRef || _.includes(m.restrictedToClassRef, displayClassRef))
          )
        );

        // only add the modules to the category if there are some left after filtering
        if (!_.isEmpty(_filteredModules)) {
          _category.modules = _filteredModules;
          accCategory[categoryKey] = _category;
        }
        return accCategory;
      },
      {}
    );
  },
  //next line builds out the cache key for this memoized function.
  //no need to include 'categories' - adding 'categories' to the cache would have to use JSON.stringify(categories) and that would impact performance
  (
    designerType,
    categories,
    accountAppVersion,
    expAppVersion,
    pageTypeRef,
    displayClassRef,
    quizType,
    experienceTypeRef
  ) =>
    `cache-${designerType}-${pageTypeRef}-${displayClassRef}-${quizType}-${accountAppVersion}-${expAppVersion}-${experienceTypeRef}`
);

/**
 * Returns the default module definitions for the given module type
 * @param {enum} moduleType the MODULE_TYPES/FIELD_TYPES enum value of the module/field we're looking up
 * @return {object} containing given module types default properties
 */
const getDefaultModuleConfig = (moduleType) => moduleData[moduleType];

/**
 * can be used to point SD modules to open up another module editor type.
 * this is only the case of 'rich headline' for now, as we want to have the module type 'headline' in the JSON as in PD but open up SD 'rich headline' module editor, see WYN-2055
 */
const sdModuleTypeOverrides = {
  [MODULE_TYPES.HEADLINE]: MODULE_TYPES.RICH_HEADLINE,
};

//this modules are keeping their data in an array of objects
const MULTI_ROW_DATA_MODULES = [
  MODULE_TYPES.IMAGE,
  MODULE_TYPES.IMAGE_GALLERY,
  MODULE_TYPES.VIDEO,
  MODULE_TYPES.VIDEO_GALLERY,
];

//tells if a module type is containing mult row data
const isMultiRowDataModule = (moduleType) => MULTI_ROW_DATA_MODULES.includes(moduleType);

//tells the prop name where the multi row data is located
const getMultiRowDataPropName = (moduleType) => {
  switch (moduleType) {
    case MODULE_TYPES.IMAGE:
    case MODULE_TYPES.IMAGE_GALLERY:
      return 'images';
    case MODULE_TYPES.VIDEO:
    case MODULE_TYPES.VIDEO_GALLERY:
      return 'videos';
  }
  return '';
};

const fieldCategoriesForChatbotFormDesigner = [
  MODULE_CATEGORIES.CONTENT,
  MODULE_CATEGORIES.STANDARD,
  MODULE_CATEGORIES.QUIZ,
];

export {
  MODULE_CATEGORIES,
  MODULE_GROUP_RESTRICTION,
  fieldCategoriesFormDesigner,
  fieldCategoriesPromotionFormDesigner,
  fieldCategoriesScreenDesigner,
  filterModuleCategories,
  footerModuleCategories,
  headerModuleCategories,
  getDefaultModuleConfig,
  getMultiRowDataPropName,
  isMultiRowDataModule,
  moduleCategories,
  moduleData,
  screenModuleCategories,
  sdModuleTypeOverrides,
  fieldCategoriesForChatbotFormDesigner,
};
export default MODULE_TYPES;
